import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Copy, Loader2, RefreshCw } from "lucide-react";
import { Header } from "@/components/Header";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/components/auth/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  clearAppTemplateContinuation,
  copyTemplatePreviewLink,
  createAppFromTemplate,
  loadPublicAppTemplates,
  rememberAppTemplateContinuation,
  templateContinuationPath,
  type PublicAppTemplate,
} from "@/lib/app-templates";
import { getOperatingSystemDetailPath } from "@/lib/operating-systems";
import { createUuid } from "@/lib/uuid";
import { PublicTemplatePreview } from "@/components/templates/PublicTemplatePreview";
import { TemplatePreviewDialog } from "@/components/templates/TemplatePreviewDialog";

export default function Templates() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAnonymous, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [templates, setTemplates] = useState<PublicAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingId, setCreatingId] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<PublicAppTemplate | null>(null);
  const continuedRef = useRef(false);
  const createInFlightRef = useRef(false);
  const createRequestIdsRef = useRef(new Map<string, string>());

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      setTemplates(await loadPublicAppTemplates());
    } catch (error) {
      toast({
        title: "Templates unavailable",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (authLoading) return;
    void loadTemplates();
  }, [authLoading, loadTemplates, user?.id]);

  const create = useCallback(async (
    templateAppId: string,
    requestId?: string,
  ) => {
    if (authLoading || createInFlightRef.current) return;
    const stableRequestId = requestId
      ?? createRequestIdsRef.current.get(templateAppId)
      ?? createUuid();
    createRequestIdsRef.current.set(templateAppId, stableRequestId);
    const continuation = templateContinuationPath(templateAppId, stableRequestId);
    rememberAppTemplateContinuation(continuation);
    if (!user?.id || isAnonymous) {
      navigate(`/beta_access?redirect=${encodeURIComponent(continuation)}`, { replace: true });
      return;
    }

    navigate(continuation, { replace: true });
    createInFlightRef.current = true;
    setCreatingId(templateAppId);
    try {
      const result = await createAppFromTemplate(templateAppId, stableRequestId);
      createRequestIdsRef.current.delete(templateAppId);
      clearAppTemplateContinuation(continuation);
      if (result.status === "failed") {
        toast({
          title: "App preparation failed",
          description: "The App is in My Apps. Open it to see the error and Retry.",
          variant: "destructive",
        });
      } else {
        toast({
          title: result.ready ? "App ready" : "Preparing your App",
          description: result.ready
            ? "Your private App is ready to use."
            : "It is now visible in My Apps and will open when preparation finishes.",
        });
      }
      navigate(getOperatingSystemDetailPath(result.app_id));
    } catch (error) {
      toast({
        title: "App creation failed",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      createInFlightRef.current = false;
      setCreatingId(null);
    }
  }, [authLoading, isAnonymous, navigate, toast, user?.id]);

  useEffect(() => {
    if (continuedRef.current || authLoading || !user?.id || isAnonymous) return;
    const templateAppId = searchParams.get("create");
    const requestId = searchParams.get("request");
    if (!templateAppId || !requestId) return;
    continuedRef.current = true;
    void create(templateAppId, requestId);
  }, [authLoading, create, isAnonymous, searchParams, user?.id]);

  const openPreview = useCallback((template: PublicAppTemplate) => {
    setPreviewTemplate(template);
    const params = new URLSearchParams(searchParams);
    params.set("template", template.id);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const closePreview = useCallback(() => {
    setPreviewTemplate(null);
    if (!searchParams.has("template")) return;
    const params = new URLSearchParams(searchParams);
    params.delete("template");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const copyTemplateLink = useCallback(async (template: PublicAppTemplate) => {
    try {
      await copyTemplatePreviewLink(template.id);
      toast({ title: "Template link copied", description: `Share ${template.name} from the Template catalog.` });
    } catch {
      toast({ title: "Could not copy link", description: "Copy the link from your browser address bar instead.", variant: "destructive" });
    }
  }, [toast]);

  useEffect(() => {
    const templateId = searchParams.get("template");
    if (!templateId || loading) return;
    const template = templates.find((item) => item.id === templateId);
    if (template) setPreviewTemplate(template);
  }, [loading, searchParams, templates]);

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="App Templates | Applyt"
        description="Browse public App Templates and create your own private App."
      />
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-medium text-primary">Public Templates</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Start with a working App
            </h1>
          </div>
          <Button variant="outline" onClick={() => void loadTemplates()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        </div>

        {loading && templates.length === 0 ? (
          <div className="flex min-h-64 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading Templates…
          </div>
        ) : templates.length === 0 ? (
          <div className="mt-10 rounded-xl border border-dashed border-border p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">No Templates yet</h2>
          </div>
        ) : (
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {templates.map((template) => (
              <article key={template.id} className="rounded-xl border border-border bg-card p-4 shadow-sm">
                <PublicTemplatePreview
                  template={template}
                  onExpand={() => openPreview(template)}
                />
                <div className="mt-4 flex items-start justify-between gap-3">
                  <h2 className="line-clamp-2 text-lg font-semibold leading-6 text-foreground">
                    {template.name}
                  </h2>
                  <Button size="sm" variant="ghost" className="shrink-0" onClick={() => void copyTemplateLink(template)}>
                    <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                    Copy link
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
      <TemplatePreviewDialog
        template={previewTemplate}
        actionsDisabled={creatingId !== null || authLoading}
        creating={creatingId === previewTemplate?.id}
        onOpenChange={(open) => {
          if (!open) closePreview();
        }}
        onCopyLink={() => {
          if (previewTemplate) void copyTemplateLink(previewTemplate);
        }}
        onCreate={() => {
          if (previewTemplate) void create(previewTemplate.id);
        }}
      />
    </div>
  );
}
